<template>
  <Content class="system-monitor">
    <Card>
      <div slot="title">
        <span>系统监控</span>
        <Button 
          type="primary" 
          size="small" 
          icon="ios-refresh"
          style="float: right;"
          @click="refresh"
        >
          刷新
        </Button>
      </div>

      <Spin v-if="loading" fix></Spin>

      <!-- 系统状态概览 -->
      <Row :gutter="16" class="status-cards">
        <Col span="6">
          <Card class="status-card" :class="systemStatus.status">
            <div class="status-header">
              <Icon type="ios-pulse" size="32" />
              <span class="status-title">系统状态</span>
            </div>
            <div class="status-value">
              <Tag :color="getStatusColor(systemStatus.status)">
                {{ getStatusText(systemStatus.status) }}
              </Tag>
            </div>
            <div class="status-info">运行时间: {{ systemStatus.uptime }}</div>
          </Card>
        </Col>
        <Col span="6">
          <Card class="status-card">
            <div class="status-header">
              <Icon type="ios-speedometer" size="32" />
              <span class="status-title">CPU使用率</span>
            </div>
            <div class="status-value">
              <Progress 
                :percent="systemStatus.cpuUsage || 0" 
                :stroke-color="getProgressColor(systemStatus.cpuUsage)"
                :stroke-width="10"
              />
            </div>
            <div class="status-info">{{ systemStatus.cpuUsage || 0 }}%</div>
          </Card>
        </Col>
        <Col span="6">
          <Card class="status-card">
            <div class="status-header">
              <Icon type="ios-grid" size="32" />
              <span class="status-title">内存使用</span>
            </div>
            <div class="status-value">
              <Progress 
                :percent="systemStatus.memoryUsage || 0" 
                :stroke-color="getProgressColor(systemStatus.memoryUsage)"
                :stroke-width="10"
              />
            </div>
            <div class="status-info">
              {{ formatBytes(systemStatus.memoryUsed) }} / {{ formatBytes(systemStatus.memoryTotal) }}
            </div>
          </Card>
        </Col>
        <Col span="6">
          <Card class="status-card">
            <div class="status-header">
              <Icon type="ios-disc" size="32" />
              <span class="status-title">磁盘使用</span>
            </div>
            <div class="status-value">
              <Progress 
                :percent="systemStatus.diskUsage || 0" 
                :stroke-color="getProgressColor(systemStatus.diskUsage)"
                :stroke-width="10"
              />
            </div>
            <div class="status-info">
              {{ formatBytes(systemStatus.diskUsed) }} / {{ formatBytes(systemStatus.diskTotal) }}
            </div>
          </Card>
        </Col>
      </Row>

      <Tabs v-model="activeTab" style="margin-top: 16px;">
        <!-- API性能监控 -->
        <TabPane label="API性能" name="api">
          <Row :gutter="16">
            <Col span="16">
              <Card title="API响应时间趋势">
                <div ref="apiTrendChart" style="height: 300px;"></div>
              </Card>
            </Col>
            <Col span="8">
              <Card title="慢接口排行">
                <div class="slow-api-list">
                  <div 
                    v-for="(api, index) in slowApis" 
                    :key="index"
                    class="slow-api-item"
                  >
                    <div class="api-rank">{{ index + 1 }}</div>
                    <div class="api-info">
                      <div class="api-path">{{ api.path }}</div>
                      <div class="api-stats">
                        <span>平均: {{ api.avgTime }}ms</span>
                        <span>最大: {{ api.maxTime }}ms</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <!-- 数据库监控 -->
        <TabPane label="数据库" name="database">
          <Row :gutter="16">
            <Col span="8">
              <Card title="连接状态">
                <div class="db-status">
                  <div class="db-item">
                    <span class="label">状态</span>
                    <Tag :color="dbStatus.connected ? 'success' : 'error'">
                      {{ dbStatus.connected ? '已连接' : '断开' }}
                    </Tag>
                  </div>
                  <div class="db-item">
                    <span class="label">活跃连接</span>
                    <span class="value">{{ dbStatus.activeConnections }}</span>
                  </div>
                  <div class="db-item">
                    <span class="label">可用连接</span>
                    <span class="value">{{ dbStatus.availableConnections }}</span>
                  </div>
                  <div class="db-item">
                    <span class="label">查询总数</span>
                    <span class="value">{{ formatNumber(dbStatus.totalQueries) }}</span>
                  </div>
                </div>
              </Card>
            </Col>
            <Col span="16">
              <Card title="数据库查询趋势">
                <div ref="dbQueryChart" style="height: 250px;"></div>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <!-- 业务监控 -->
        <TabPane label="业务监控" name="business">
          <Row :gutter="16">
            <Col span="6" v-for="item in businessMetrics" :key="item.key">
              <Card class="business-card">
                <div class="metric-icon" :style="{ backgroundColor: item.color }">
                  <Icon :type="item.icon" size="24" color="#fff" />
                </div>
                <div class="metric-info">
                  <div class="metric-value">{{ item.value }}</div>
                  <div class="metric-label">{{ item.label }}</div>
                  <div class="metric-status" v-if="item.status">
                    <Icon 
                      :type="item.status === 'warning' ? 'ios-warning' : 'ios-checkmark-circle'" 
                      :color="item.status === 'warning' ? '#E6A23C' : '#67C23A'"
                    />
                    <span :style="{ color: item.status === 'warning' ? '#E6A23C' : '#67C23A' }">
                      {{ item.statusText }}
                    </span>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <!-- 告警规则 -->
        <TabPane label="告警规则" name="alerts">
          <div style="margin-bottom: 16px;">
            <Button type="primary" icon="ios-add" @click="showAddRuleModal">
              添加规则
            </Button>
          </div>
          <Table :columns="alertColumns" :data="alertRules" border stripe>
            <template slot-scope="{ row }" slot="enabled">
              <i-switch v-model="row.enabled" @on-change="toggleRule(row)" />
            </template>
            <template slot-scope="{ row }" slot="action">
              <Button type="text" size="small" @click="editRule(row)">编辑</Button>
              <Button type="text" size="small" @click="deleteRule(row)">删除</Button>
            </template>
          </Table>
        </TabPane>

        <!-- 告警历史 -->
        <TabPane label="告警历史" name="history">
          <Table :columns="historyColumns" :data="alertHistory" border stripe>
            <template slot-scope="{ row }" slot="level">
              <Tag :color="getLevelColor(row.level)">{{ getLevelText(row.level) }}</Tag>
            </template>
            <template slot-scope="{ row }" slot="status">
              <Tag :color="row.resolved ? 'success' : 'warning'">
                {{ row.resolved ? '已处理' : '未处理' }}
              </Tag>
            </template>
          </Table>
          <Page 
            v-show="historyTotal > 0"
            :total="historyTotal"
            :current="historyPage"
            :page-size="10"
            show-total
            @on-change="loadAlertHistory"
            style="margin-top: 16px; text-align: right;"
          />
        </TabPane>
      </Tabs>
    </Card>

    <!-- 添加/编辑告警规则弹窗 -->
    <Modal 
      v-model="ruleModal" 
      :title="isEditRule ? '编辑告警规则' : '添加告警规则'"
      width="500"
      @on-ok="saveRule"
    >
      <Form :model="ruleForm" :label-width="100" ref="ruleForm">
        <FormItem label="规则名称" prop="name">
          <Input v-model="ruleForm.name" placeholder="请输入规则名称" />
        </FormItem>
        <FormItem label="监控类型" prop="type">
          <Select v-model="ruleForm.type">
            <Option value="cpu">CPU使用率</Option>
            <Option value="memory">内存使用率</Option>
            <Option value="disk">磁盘使用率</Option>
            <Option value="api">API响应时间</Option>
            <Option value="error">错误数量</Option>
            <Option value="order">订单异常</Option>
            <Option value="car">车辆离线</Option>
            <Option value="stock">库存预警</Option>
          </Select>
        </FormItem>
        <FormItem label="告警条件" prop="condition">
          <Select v-model="ruleForm.condition" style="width: 100px;">
            <Option value="gt">大于</Option>
            <Option value="lt">小于</Option>
            <Option value="eq">等于</Option>
            <Option value="gte">大于等于</Option>
            <Option value="lte">小于等于</Option>
          </Select>
          <InputNumber v-model="ruleForm.threshold" style="width: 150px; margin-left: 8px;" />
          <span style="margin-left: 8px;">{{ getThresholdUnit(ruleForm.type) }}</span>
        </FormItem>
        <FormItem label="告警级别" prop="level">
          <Select v-model="ruleForm.level">
            <Option value="info">信息</Option>
            <Option value="warning">警告</Option>
            <Option value="error">错误</Option>
            <Option value="critical">严重</Option>
          </Select>
        </FormItem>
        <FormItem label="通知方式" prop="notifyMethods">
          <CheckboxGroup v-model="ruleForm.notifyMethods">
            <Checkbox label="system">站内消息</Checkbox>
            <Checkbox label="email">邮件</Checkbox>
            <Checkbox label="sms">短信</Checkbox>
          </CheckboxGroup>
        </FormItem>
        <FormItem label="启用状态">
          <i-switch v-model="ruleForm.enabled" />
        </FormItem>
      </Form>
    </Modal>
  </Content>
</template>

<script>
import { 
  getSystemStatus, 
  getApiPerformance, 
  getDatabaseStatus, 
  getBusinessMonitor,
  getAlertRules,
  saveAlertRule,
  updateAlertRule,
  deleteAlertRule,
  getAlertHistory
} from '@/api/monitor'
import Echarts from 'echarts'

export default {
  name: 'SystemMonitor',
  data() {
    return {
      loading: false,
      activeTab: 'api',
      systemStatus: {
        status: 'normal',
        uptime: '-',
        cpuUsage: 0,
        memoryUsage: 0,
        memoryUsed: 0,
        memoryTotal: 0,
        diskUsage: 0,
        diskUsed: 0,
        diskTotal: 0
      },
      slowApis: [],
      dbStatus: {
        connected: true,
        activeConnections: 0,
        availableConnections: 0,
        totalQueries: 0
      },
      businessMetrics: [],
      alertRules: [],
      alertHistory: [],
      historyTotal: 0,
      historyPage: 1,
      apiTrendChart: null,
      dbQueryChart: null,
      ruleModal: false,
      isEditRule: false,
      ruleForm: {
        name: '',
        type: 'cpu',
        condition: 'gt',
        threshold: 80,
        level: 'warning',
        notifyMethods: ['system'],
        enabled: true
      },
      alertColumns: [
        { title: '规则名称', key: 'name', width: 150 },
        { title: '监控类型', key: 'typeName', width: 120 },
        { title: '告警条件', key: 'conditionText', width: 150 },
        { title: '告警级别', key: 'levelName', width: 100 },
        { title: '启用状态', slot: 'enabled', width: 100, align: 'center' },
        { title: '操作', slot: 'action', width: 120, align: 'center' }
      ],
      historyColumns: [
        { title: '告警时间', key: 'createTime', width: 180 },
        { title: '规则名称', key: 'ruleName', width: 150 },
        { title: '告警级别', slot: 'level', width: 100 },
        { title: '告警内容', key: 'message' },
        { title: '状态', slot: 'status', width: 100 },
        { title: '处理时间', key: 'resolvedTime', width: 180 }
      ],
      refreshTimer: null
    }
  },
  methods: {
    async loadSystemStatus() {
      try {
        const res = await getSystemStatus()
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.systemStatus = res.data.data || this.systemStatus
        }
      } catch (error) {
        console.error('获取系统状态失败:', error)
      }
    },
    async loadApiPerformance() {
      try {
        const res = await getApiPerformance({ period: '24h' })
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.slowApis = res.data.data?.slowApis || []
          this.$nextTick(() => {
            this.initApiTrendChart(res.data.data?.trend || [])
          })
        }
      } catch (error) {
        console.error('获取API性能失败:', error)
      }
    },
    async loadDatabaseStatus() {
      try {
        const res = await getDatabaseStatus()
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.dbStatus = res.data.data || this.dbStatus
          this.$nextTick(() => {
            this.initDbQueryChart(res.data.data?.queryTrend || [])
          })
        }
      } catch (error) {
        console.error('获取数据库状态失败:', error)
      }
    },
    async loadBusinessMonitor() {
      try {
        const res = await getBusinessMonitor()
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.businessMetrics = res.data.data?.metrics || []
        }
      } catch (error) {
        console.error('获取业务监控失败:', error)
      }
    },
    async loadAlertRules() {
      try {
        const res = await getAlertRules()
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.alertRules = res.data.data || []
        }
      } catch (error) {
        console.error('获取告警规则失败:', error)
      }
    },
    async loadAlertHistory(page = 1) {
      this.historyPage = page
      try {
        const res = await getAlertHistory({ page, pageSize: 10 })
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.alertHistory = res.data.data?.list || []
          this.historyTotal = res.data.data?.total || 0
        }
      } catch (error) {
        console.error('获取告警历史失败:', error)
      }
    },
    async refresh() {
      this.loading = true
      await Promise.all([
        this.loadSystemStatus(),
        this.loadApiPerformance(),
        this.loadDatabaseStatus(),
        this.loadBusinessMonitor()
      ])
      this.loading = false
    },
    initApiTrendChart(data) {
      if (!this.$refs.apiTrendChart) return
      
      if (this.apiTrendChart) {
        this.apiTrendChart.dispose()
      }
      
      this.apiTrendChart = Echarts.init(this.$refs.apiTrendChart)
      
      const option = {
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: ['平均响应时间', '请求数']
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: data.map(item => item.time)
        },
        yAxis: [
          {
            type: 'value',
            name: '响应时间(ms)',
            position: 'left'
          },
          {
            type: 'value',
            name: '请求数',
            position: 'right'
          }
        ],
        series: [
          {
            name: '平均响应时间',
            type: 'line',
            smooth: true,
            data: data.map(item => item.avgTime),
            itemStyle: { color: '#409EFF' }
          },
          {
            name: '请求数',
            type: 'bar',
            yAxisIndex: 1,
            data: data.map(item => item.requestCount),
            itemStyle: { color: '#67C23A' }
          }
        ]
      }
      
      this.apiTrendChart.setOption(option)
    },
    initDbQueryChart(data) {
      if (!this.$refs.dbQueryChart) return
      
      if (this.dbQueryChart) {
        this.dbQueryChart.dispose()
      }
      
      this.dbQueryChart = Echarts.init(this.$refs.dbQueryChart)
      
      const option = {
        tooltip: {
          trigger: 'axis'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: data.map(item => item.time)
        },
        yAxis: {
          type: 'value',
          name: '查询数'
        },
        series: [
          {
            type: 'line',
            smooth: true,
            data: data.map(item => item.queryCount),
            itemStyle: { color: '#409EFF' },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(64,158,255,0.3)' },
                  { offset: 1, color: 'rgba(64,158,255,0.05)' }
                ]
              }
            }
          }
        ]
      }
      
      this.dbQueryChart.setOption(option)
    },
    showAddRuleModal() {
      this.isEditRule = false
      this.ruleForm = {
        name: '',
        type: 'cpu',
        condition: 'gt',
        threshold: 80,
        level: 'warning',
        notifyMethods: ['system'],
        enabled: true
      }
      this.ruleModal = true
    },
    editRule(row) {
      this.isEditRule = true
      this.ruleForm = { ...row }
      this.ruleModal = true
    },
    async saveRule() {
      try {
        const api = this.isEditRule ? updateAlertRule(this.ruleForm._id, this.ruleForm) : saveAlertRule(this.ruleForm)
        const res = await api
        
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.$Message.success(this.isEditRule ? '更新成功' : '添加成功')
          this.ruleModal = false
          this.loadAlertRules()
        } else {
          this.$Message.error(res.data?.message || '操作失败')
        }
      } catch (error) {
        this.$Message.error('操作失败')
      }
    },
    async toggleRule(row) {
      try {
        await updateAlertRule(row._id, { enabled: row.enabled })
        this.$Message.success(row.enabled ? '已启用' : '已禁用')
      } catch (error) {
        this.$Message.error('操作失败')
        row.enabled = !row.enabled
      }
    },
    deleteRule(row) {
      this.$Modal.confirm({
        title: '确认删除',
        content: `确定要删除规则"${row.name}"吗？`,
        onOk: async () => {
          try {
            const res = await deleteAlertRule(row._id)
            if (res.data && (res.data.success || res.data.code === 200)) {
              this.$Message.success('删除成功')
              this.loadAlertRules()
            }
          } catch (error) {
            this.$Message.error('删除失败')
          }
        }
      })
    },
    getStatusColor(status) {
      const colors = {
        normal: 'success',
        warning: 'warning',
        error: 'error',
        critical: 'error'
      }
      return colors[status] || 'default'
    },
    getStatusText(status) {
      const texts = {
        normal: '正常',
        warning: '警告',
        error: '异常',
        critical: '严重'
      }
      return texts[status] || '未知'
    },
    getProgressColor(percent) {
      if (percent >= 90) return '#F56C6C'
      if (percent >= 70) return '#E6A23C'
      return '#67C23A'
    },
    getLevelColor(level) {
      const colors = {
        info: 'primary',
        warning: 'warning',
        error: 'error',
        critical: 'error'
      }
      return colors[level] || 'default'
    },
    getLevelText(level) {
      const texts = {
        info: '信息',
        warning: '警告',
        error: '错误',
        critical: '严重'
      }
      return texts[level] || '未知'
    },
    getThresholdUnit(type) {
      const units = {
        cpu: '%',
        memory: '%',
        disk: '%',
        api: 'ms',
        error: '次',
        order: '个',
        car: '辆',
        stock: '件'
      }
      return units[type] || ''
    },
    formatBytes(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    formatNumber(num) {
      if (!num) return 0
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
      return num
    },
    startAutoRefresh() {
      this.refreshTimer = setInterval(() => {
        this.loadSystemStatus()
      }, 30000) // 每30秒自动刷新系统状态
    },
    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    }
  },
  mounted() {
    this.refresh()
    this.loadAlertRules()
    this.loadAlertHistory()
    this.startAutoRefresh()
    
    window.addEventListener('resize', () => {
      this.apiTrendChart?.resize()
      this.dbQueryChart?.resize()
    })
  },
  beforeDestroy() {
    this.stopAutoRefresh()
    this.apiTrendChart?.dispose()
    this.dbQueryChart?.dispose()
  }
}
</script>

<style lang="less" scoped>
.system-monitor {
  .status-cards {
    .status-card {
      padding: 20px;
      
      &.warning {
        border-left: 4px solid #E6A23C;
      }
      
      &.error, &.critical {
        border-left: 4px solid #F56C6C;
      }
      
      .status-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;
        
        .status-title {
          font-size: 14px;
          color: #909399;
        }
      }
      
      .status-value {
        margin-bottom: 8px;
      }
      
      .status-info {
        font-size: 12px;
        color: #909399;
      }
    }
  }
  
  .slow-api-list {
    .slow-api-item {
      display: flex;
      align-items: center;
      padding: 12px 0;
      border-bottom: 1px solid #f0f0f0;
      
      &:last-child {
        border-bottom: none;
      }
      
      .api-rank {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: #409EFF;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        margin-right: 12px;
        
        &:nth-child(1) {
          background: #F56C6C;
        }
      }
      
      &:nth-child(1) .api-rank {
        background: #F56C6C;
      }
      
      &:nth-child(2) .api-rank {
        background: #E6A23C;
      }
      
      &:nth-child(3) .api-rank {
        background: #67C23A;
      }
      
      .api-info {
        flex: 1;
        
        .api-path {
          font-size: 13px;
          color: #303133;
          margin-bottom: 4px;
        }
        
        .api-stats {
          font-size: 12px;
          color: #909399;
          
          span {
            margin-right: 12px;
          }
        }
      }
    }
  }
  
  .db-status {
    .db-item {
      display: flex;
      justify-content: space-between;
      padding: 12px 0;
      border-bottom: 1px solid #f0f0f0;
      
      &:last-child {
        border-bottom: none;
      }
      
      .label {
        color: #909399;
      }
      
      .value {
        font-weight: 500;
      }
    }
  }
  
  .business-card {
    display: flex;
    align-items: center;
    padding: 20px;
    
    .metric-icon {
      width: 50px;
      height: 50px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 16px;
    }
    
    .metric-info {
      flex: 1;
      
      .metric-value {
        font-size: 24px;
        font-weight: bold;
        color: #2c3e50;
      }
      
      .metric-label {
        font-size: 14px;
        color: #909399;
        margin-top: 4px;
      }
      
      .metric-status {
        font-size: 12px;
        margin-top: 8px;
        display: flex;
        align-items: center;
        gap: 4px;
      }
    }
  }
}
</style>

